import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import FeedbackListItem from '../components/feedback/FeedbackListItem';
import FeedbackDetailView from '../components/feedback/FeedbackDetailView';
import FeedbackHeader from '../components/feedback/FeedbackHeader';
import { useToast } from '../components/ui/use-toast';
import { Card, CardContent } from '../components/ui/card';
import { Star, MessageSquare, AlertCircle, Heart, Loader2, ChevronDown, ChevronUp, Search, AlertTriangle, CheckCircle2, RefreshCw, Calendar as CalendarIcon, ChevronLeft } from 'lucide-react';
import { debounce } from 'lodash';
import { Button } from '../components/ui/button';
import { getThemeColor, getTransition, getRadius, getFontSize, getFontWeight } from '../lib/theme';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { cn } from '../lib/utils';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useTimeRange } from '../hooks/useTimeRange';
import { fetchFeedback, fetchFeedbackSubmissions, testFirebaseConnection } from '../services/analyticsService';
import { collection, query, where, orderBy, limit, getDocs, addDoc, Timestamp, doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchUserDataForFeedback } from '../lib/utils';

// Add a UUID generator function
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Function to create sample user data in Firebase
const createSampleUserData = async () => {
  try {
    console.log('🔄 Creating sample user data in Firebase...');

    const sampleUsers = [
      {
        email: 'john.doe@example.com',
        displayName: 'John Doe',
        isSubscribed: true,
        subscriptionPlan: 'Pro Monthly',
        createdAt: Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)), // 30 days ago
        photoURL: null
      },
      {
        email: 'jane.smith@example.com',
        displayName: 'Jane Smith',
        hasPaid: true,
        createdAt: Timestamp.fromDate(new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)), // 15 days ago
        photoURL: null
      },
      {
        email: 'bob.wilson@example.com',
        displayName: 'Bob Wilson',
        isSubscribed: false,
        hasPaid: false,
        createdAt: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), // 7 days ago
        photoURL: null
      }
    ];

    // Add sample users to users collection
    for (let i = 0; i < sampleUsers.length; i++) {
      const userId = `sample_user_${i + 1}`;
      await setDoc(doc(db, 'users', userId), sampleUsers[i]);
    }

    console.log('✅ Sample user data created successfully');
    return true;
  } catch (error) {
    console.error('❌ Error creating sample user data:', error);
    return false;
  }
};

// Function to create sample feedback data in Firebase
const createSampleFeedbackData = async () => {
  try {
    console.log('🔄 Creating sample feedback data in Firebase...');

    const sampleFeedback = [
      {
        userId: 'sample_user_1',
        resumeId: 'resume_001',
        emoji: '😊',
        comment: 'Great experience with the AI features! The autofill suggestions are really helpful.',
        timestamp: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)), // 2 days ago
        context: 'post_download',
        type: 'feature_request',
        status: 'resolved',
        priority: 'low',
        upvoteCount: 3
      },
      {
        userId: 'sample_user_2',
        resumeId: 'resume_002',
        emoji: '🤔',
        comment: 'The resume builder is good but could use more templates. Would love to see more creative options.',
        timestamp: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)), // 1 day ago
        context: 'post_download',
        type: 'feature_request',
        status: 'assigned',
        priority: 'medium',
        assignedTo: 'admin_dilhan',
        upvoteCount: 1
      },
      {
        userId: 'sample_user_3',
        resumeId: 'resume_003',
        emoji: '😞',
        comment: 'Found a bug when downloading the resume. The PDF format is corrupted.',
        timestamp: Timestamp.fromDate(new Date()),
        context: 'post_download',
        type: 'bug',
        status: 'new',
        priority: 'critical',
        upvoteCount: 5
      }
    ];

    // Add sample data to feedback collection
    for (const feedback of sampleFeedback) {
      await addDoc(collection(db, 'feedback'), feedback);
    }

    console.log('✅ Sample feedback data created successfully');
    return true;
  } catch (error) {
    console.error('❌ Error creating sample feedback data:', error);
    return false;
  }
};

const Feedback = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);
  const [feedbackData, setFeedbackData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [isStatsExpanded, setIsStatsExpanded] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const searchTimeout = useRef(null);
  const lastScrollY = useRef(0);
  const scrollThreshold = 100;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [showDetailView, setShowDetailView] = useState(false);
  const { timeRange, getDateRange } = useTimeRange();

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Optimized search handler with debounce
  const handleSearch = useCallback((value) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
  }, []);

  // Fetch real feedback data from Firebase
  const fetchFeedbackData = useCallback(async (pageNum = 1) => {
    setIsLoading(true);
    try {
      console.log('🔄 Fetching feedback data for page:', pageNum);

      const { start: startDate, end: endDate } = getDateRange();
      console.log('📅 Date range:', { startDate, endDate });

      // Fetch feedback data from the feedback collection
      const allFeedbackData = [];

      try {
        console.log('📊 Fetching from feedback collection...');
        const feedbackQuery = query(
          collection(db, 'feedback'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );

        const feedbackSnapshot = await getDocs(feedbackQuery);
        console.log('📊 Feedback collection results:', feedbackSnapshot.size, 'documents');

        feedbackSnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('📊 Feedback doc data:', { id: doc.id, ...data });

          // Only include events within the date range
          const eventDate = new Date(data.timestamp?.toDate?.() || data.timestamp);

          // Add safety checks for undefined dates
          if (!startDate || !endDate) {
            console.log('⚠️ Date range is undefined, including all feedback');
            allFeedbackData.push({
              id: doc.id,
              ...data,
              source: 'feedback_collection'
            });
            return;
          }

          console.log('📅 Event date:', eventDate, 'Start date:', startDate, 'End date:', endDate);
          console.log('📅 Date comparison:', {
            eventDate: eventDate.toISOString(),
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            isAfterStart: eventDate >= startDate,
            isBeforeEnd: eventDate <= endDate
          });

          // Temporarily disable date filtering to debug
          console.log('✅ Feedback included (date filtering disabled for debug)');
          allFeedbackData.push({
            id: doc.id,
            ...data,
            source: 'feedback_collection'
          });

          // TODO: Re-enable date filtering after debug
          // if (eventDate >= startDate && eventDate <= endDate) {
          //   console.log('✅ Feedback included in date range');
          //   allFeedbackData.push({
          //     id: doc.id,
          //     ...data,
          //     source: 'feedback_collection'
          //   });
          // } else {
          //   console.log('❌ Feedback filtered out by date range');
          // }
        });
      } catch (error) {
        console.log('⚠️ Error fetching from feedback collection:', error);
      }

      console.log('📋 Total feedback data collected:', allFeedbackData.length, 'items');
      if (allFeedbackData.length > 0) {
        console.log('📋 Sample feedback data:', allFeedbackData[0]);
      } else {
        console.log('⚠️ No feedback data collected from Firebase');
      }

      // Fetch user data for all feedback items
      console.log('🔄 Fetching user data for feedback items...');
      const feedbackWithUserData = await fetchUserDataForFeedback(allFeedbackData);
      console.log('✅ User data fetched for feedback items');

      // Transform Firebase data to match our component structure
      const transformedData = feedbackWithUserData.map((item, index) => {
        // Handle the actual Firebase feedback structure based on the documentation
        const comment = item.comment || 'No message provided';
        const timestamp = item.timestamp;
        const userId = item.userId || 'Anonymous User';
        const emoji = item.emoji || '📝';
        const context = item.context || 'post_download';
        const resumeId = item.resumeId || 'unknown';

        // Enhanced user and resume data
        const userData = item.userData; // This now contains the fetched user data from Firestore
        const resumeData = item.resumeData;

        // Get user display info from fetched user data
        const userDisplayName = userData?.displayName || userData?.email?.split('@')[0] || userId;
        const userEmail = userData?.email || `${userId}@example.com`;
        const userAvatar = userData?.photoURL || null;

        // Get resume display info
        const resumeTitle = resumeData?.title || `Resume ${resumeId}`;
        const resumeTemplate = resumeData?.template || 'Unknown Template';
        const resumeSections = resumeData?.sections || ['Experience', 'Education', 'Skills'];
        const resumeLastUpdated = resumeData?.lastUpdated || timestamp;

        // Convert emoji to rating for display purposes
        let rating = 3; // Default rating
        if (emoji) {
          if (emoji.includes('😃') || emoji.includes('😊') || emoji.includes('😄') || emoji.includes('😍') || emoji.includes('👍') || emoji.includes('❤️')) {
            rating = 5;
          } else if (emoji.includes('😐') || emoji.includes('🤔') || emoji.includes('😶')) {
            rating = 3;
          } else if (emoji.includes('😞') || emoji.includes('😡') || emoji.includes('👎') || emoji.includes('😭')) {
            rating = 1;
          }
        }

        // Determine sentiment based on emoji and comment content
        let sentiment = 'neutral';

        // Use emoji to determine sentiment
        if (emoji && typeof emoji === 'string') {
          if (emoji.includes('😃') || emoji.includes('😊') || emoji.includes('😄') || emoji.includes('😍') || emoji.includes('👍') || emoji.includes('❤️')) {
            sentiment = 'positive';
          } else if (emoji.includes('😞') || emoji.includes('😡') || emoji.includes('👎') || emoji.includes('😭')) {
            sentiment = 'negative';
          } else if (emoji.includes('😐') || emoji.includes('🤔') || emoji.includes('😶')) {
            sentiment = 'neutral';
          }
        }

        // Override with comment content analysis if no clear sentiment from emoji
        if (sentiment === 'neutral' && comment) {
          const message = comment.toLowerCase();
          if (message.includes('great') || message.includes('good') || message.includes('love') || message.includes('excellent') || message.includes('amazing') || message.includes('perfect') || message.includes('liked') || message.includes('awesome')) {
            sentiment = 'positive';
          } else if (message.includes('bad') || message.includes('terrible') || message.includes('hate') || message.includes('awful') || message.includes('horrible') || message.includes('worst') || message.includes('dislike')) {
            sentiment = 'negative';
          }
        }

        // Determine category based on context and comment content
        let category = 'Resume Feedback';
        if (context) {
          if (context === 'post_download') {
            category = 'Resume Download';
          } else if (context.includes('ai') || context.includes('suggestion')) {
            category = 'AI Features';
          } else if (context.includes('bug') || context.includes('error')) {
            category = 'Bug Report';
          } else if (context.includes('feature')) {
            category = 'Feature Request';
          }
        }

        // Also check comment content for category
        if (comment) {
          const message = comment.toLowerCase();
          if (message.includes('ai') || message.includes('suggestion') || message.includes('autofill') || message.includes('optimize')) {
            category = 'AI Features';
          } else if (message.includes('bug') || message.includes('error') || message.includes('broken') || message.includes('issue') || message.includes('problem')) {
            category = 'Bug Report';
          } else if (message.includes('feature') || message.includes('request') || message.includes('add') || message.includes('want')) {
            category = 'Feature Request';
          } else if (message.includes('slow') || message.includes('performance') || message.includes('speed') || message.includes('loading')) {
            category = 'Performance';
          } else if (message.includes('ui') || message.includes('design') || message.includes('look') || message.includes('interface')) {
            category = 'UI/UX';
          } else if (message.includes('template') || message.includes('resume') || message.includes('download')) {
            category = 'Resume Download';
          }
        }

        // Use admin fields if available, otherwise determine from sentiment
        const adminType = item.type || 'feedback';
        const adminStatus = item.status || 'new';
        const adminPriority = item.priority || 'medium';
        const assignedTo = item.assignedTo || null;
        const internalNote = item.internalNote || '';
        const upvoteCount = item.upvoteCount || 0;

        // Determine priority based on admin field or sentiment
        let priority = adminPriority;
        if (adminPriority === 'medium' && sentiment === 'negative' && category === 'Bug Report') {
          priority = 'high';
        } else if (adminPriority === 'medium' && sentiment === 'positive') {
          priority = 'low';
        }

        // Determine status based on admin field or sentiment
        let status = adminStatus;
        if (adminStatus === 'new') {
          if (sentiment === 'positive') {
            status = 'resolved';
          } else if (sentiment === 'negative') {
            status = 'in_progress';
          }
        }

        return {
          id: item.id || generateUUID(),
          numericId: `FB-${String(index + 1).padStart(3, '0')}`,
          user: userDisplayName,
          email: userEmail,
          avatar: userAvatar,
          rating: rating,
          comment: comment,
          date: timestamp ? new Date(timestamp.toDate?.() || timestamp).toISOString() : new Date().toISOString(),
          sentiment: sentiment,
          category: category,
          status: status,
          priority: priority,
          tags: [category.toLowerCase().replace(' ', '-'), sentiment, context, adminType],
          replies: 0, // TODO: Add replies to Firebase schema
          likes: upvoteCount,
          isResolved: status === 'resolved',
          lastActivity: timestamp ? new Date(timestamp.toDate?.() || timestamp).toISOString() : new Date().toISOString(),
          resumeId: resumeId,
          emoji: emoji,
          context: context,
          // Admin fields
          type: adminType,
          assignedTo: assignedTo,
          internalNote: internalNote,
          upvoteCount: upvoteCount,
          // Enhanced resume info
          resumeInfo: {
            title: resumeTitle,
            lastUpdated: resumeLastUpdated ? new Date(resumeLastUpdated.toDate?.() || resumeLastUpdated).toISOString() : new Date().toISOString(),
            version: 'v1.0',
            template: resumeTemplate,
            sections: resumeSections,
            aiOptimized: resumeData?.aiOptimized || false,
            optimizationScore: resumeData?.optimizationScore || 0,
            keywords: resumeData?.keywords || [],
            matchScore: resumeData?.matchScore || 0,
            targetRole: resumeData?.targetRole || 'Unknown',
            targetCompanies: resumeData?.targetCompanies || []
          },
          // Raw data for debugging
          userData: userData,
          resumeData: resumeData
        };
      });

      // REMOVED: No more fallback dummy data - only show real data
      if (transformedData.length === 0) {
        console.log('⚠️ No real feedback data found - showing empty state');
        toast({
          title: 'No Feedback Data',
          description: 'No feedback data found in the selected time range. Would you like to create some sample data?',
          variant: 'info',
        });
      }

      if (pageNum === 1) {
        setFeedbackData(transformedData);
      } else {
        setFeedbackData(prev => [...prev, ...transformedData]);
      }

      // Set hasMore based on actual data
      setHasMore(transformedData.length === 100); // If we got 100 items, there might be more
      setPage(pageNum);

      console.log('✅ Feedback data transformed and set:', transformedData.length, 'items');
      if (transformedData.length > 0) {
        console.log('✅ First transformed item:', transformedData[0]);
      }
    } catch (error) {
      console.error('❌ Error fetching feedback data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch feedback data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, getDateRange]);

  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      // First test Firebase connection
      console.log('🔍 Testing Firebase connection...');
      const isConnected = await testFirebaseConnection();
      console.log('🔍 Firebase connection status:', isConnected);

      if (!isConnected) {
        console.error('❌ Firebase connection failed');
        toast({
          title: 'Connection Error',
          description: 'Unable to connect to Firebase. Using sample data.',
          variant: 'destructive',
        });
      }

      await fetchFeedbackData(1);

      // Show welcome toast only once
      const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcome');
      if (!hasSeenWelcome) {
        toast({
          title: "Welcome to Feedback",
          description: "Your feedback dashboard is ready.",
          variant: "info",
        });
        sessionStorage.setItem('hasSeenWelcome', 'true');
      }
    };

    initializeData();
  }, [fetchFeedbackData, toast]);

  // Refetch data when time range changes
  useEffect(() => {
    fetchFeedbackData(1);
  }, [timeRange, fetchFeedbackData]);

  // Function to create sample data for testing
  const handleCreateSampleData = async () => {
    try {
      console.log('🔄 Creating sample data...');
      await createSampleUserData();
      await createSampleFeedbackData();

      toast({
        title: 'Sample Data Created',
        description: 'Sample user and feedback data has been created in Firebase.',
        duration: 3000,
      });

      // Refresh the data
      await fetchFeedbackData(1);
    } catch (error) {
      console.error('❌ Error creating sample data:', error);
      toast({
        title: 'Error',
        description: 'Failed to create sample data. Please check the console for details.',
        variant: 'destructive',
        duration: 3000,
      });
    }
  };

  // Optimize status update handler
  const handleStatusUpdate = useCallback(async (updatedFeedback) => {
    console.log('🔄 Updating feedback status:', updatedFeedback);

    try {
      // Update in Firebase
      const feedbackRef = doc(db, 'feedback', updatedFeedback.id);
      const updateData = {};

      if (updatedFeedback.status) {
        updateData.status = updatedFeedback.status;
      }
      if (updatedFeedback.priority) {
        updateData.priority = updatedFeedback.priority;
      }
      if (updatedFeedback.assignedTo) {
        updateData.assignedTo = updatedFeedback.assignedTo;
      }

      await updateDoc(feedbackRef, updateData);
      console.log('✅ Feedback updated in Firebase');

      // Update local state
      setFeedbackData(prev => {
        const newData = prev.map(f => {
          if (f.id === updatedFeedback.id) {
            let newFeedback = { ...f };

            // Handle status updates
            if (updatedFeedback.status) {
              newFeedback.status = updatedFeedback.status;
              console.log('✅ Status updated to:', updatedFeedback.status);

              // Update priority based on status
              if (updatedFeedback.status === 'resolved') {
                newFeedback.priority = 'low';
              } else if (updatedFeedback.status === 'in_progress') {
                newFeedback.priority = 'medium';
              }
            }

            // Handle priority updates
            if (updatedFeedback.priority === 'critical') {
              newFeedback.priority = 'critical';
              newFeedback.status = 'new'; // Critical items should be new
            }

            return newFeedback;
          }
          return f;
        });

        // If the updated feedback is selected, update it in the selected state
        if (selectedFeedback?.id === updatedFeedback.id) {
          const updatedSelected = newData.find(f => f.id === updatedFeedback.id);
          setSelectedFeedback(updatedSelected);
          console.log('✅ Selected feedback updated:', updatedSelected);
        }

        return newData;
      });

      toast({
        title: "Status Updated",
        description: `Feedback status updated to ${updatedFeedback.status || 'new status'}`,
        variant: "success",
      });

    } catch (error) {
      console.error('❌ Error updating feedback:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update feedback status. Please try again.",
        variant: "destructive",
      });
    }
  }, [selectedFeedback, toast]);

  // Optimize stats calculation with proper dependency tracking
  const stats = useMemo(() => {
    if (!feedbackData.length) {
      return {
        total: 0,
        critical: 0,
        resolved: 0,
        averageRating: '0',
        responseRate: 0,
        openIssues: 0,
        satisfaction: 0,
        inProgress: 0
      };
    }

    const totalReviews = feedbackData.length;
    const criticalIssues = feedbackData.filter(f => f.priority === 'critical').length;
    const resolvedIssues = feedbackData.filter(f => f.status === 'resolved').length;
    const newIssues = feedbackData.filter(f => f.status === 'new').length;
    const inProgressIssues = feedbackData.filter(f => f.status === 'in_progress').length;
    const assignedIssues = feedbackData.filter(f => f.status === 'assigned').length;
    const averageRating = (feedbackData.reduce((sum, f) => sum + f.rating, 0) / totalReviews).toFixed(1);
    const responseRate = Math.round((feedbackData.filter(f => f.replies > 0).length / totalReviews) * 100);
    const satisfaction = Math.round((feedbackData.filter(f => f.rating >= 4).length / totalReviews) * 100);

    return {
      total: totalReviews,
      critical: criticalIssues,
      resolved: resolvedIssues,
      averageRating,
      responseRate,
      newIssues,
      satisfaction,
      inProgress: inProgressIssues,
      assigned: assignedIssues
    };
  }, [feedbackData]); // Make sure it updates when feedbackData changes

  // Update filteredFeedback to use only global time range
  const filteredFeedback = useMemo(() => {
    console.log('🔍 Filtering feedback data:', {
      feedbackDataLength: feedbackData.length,
      searchQuery,
      statusFilter,
      sortBy,
      timeRange
    });

    if (!feedbackData.length) {
      console.log('❌ No feedback data to filter');
      return [];
    }

    const searchLower = searchQuery.toLowerCase();
    const hasSearch = searchLower.length > 0;
    const { start, end } = getDateRange();

    console.log('📅 Date range for filtering:', { start: start?.toISOString(), end: end?.toISOString() });

    let filtered = feedbackData.filter(feedback => {
      // Status filter
      if (statusFilter !== 'all' && feedback.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (hasSearch) {
        const matchesSearch = (
          feedback.comment.toLowerCase().includes(searchLower) ||
          feedback.user.toLowerCase().includes(searchLower) ||
          feedback.category.toLowerCase().includes(searchLower) ||
          feedback.tags.some(tag => tag.toLowerCase().includes(searchLower))
        );

        if (!matchesSearch) {
          return false;
        }
      }

      return true;
    });

    // Sort feedback
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.date) - new Date(a.date);
        case 'status':
          const statusOrder = { 'new': 0, 'assigned': 1, 'in_progress': 2, 'resolved': 3, 'wont_fix': 4 };
          return statusOrder[a.status] - statusOrder[b.status];
        case 'priority':
          const priorityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    console.log('✅ Filtered and sorted feedback count:', filtered.length);
    return filtered;
  }, [feedbackData, searchQuery, statusFilter, sortBy, timeRange, getDateRange]);

  // Handle feedback selection with animation
  const handleFeedbackSelect = useCallback((feedback) => {
    // Directly set the selected feedback
    setSelectedFeedback(feedback);
  }, []);

  // Set first feedback as selected by default
  useEffect(() => {
    if (filteredFeedback.length > 0 && !selectedFeedback) {
      setSelectedFeedback(filteredFeedback[0]);
    } else if (filteredFeedback.length > 0 && selectedFeedback) {
      // If the currently selected feedback is not in the filtered list, select the first one
      const isSelectedInFiltered = filteredFeedback.some(f => f.id === selectedFeedback.id);
      if (!isSelectedInFiltered) {
        setSelectedFeedback(filteredFeedback[0]);
      }
    } else if (filteredFeedback.length === 0) {
      setSelectedFeedback(null);
    }
  }, [filteredFeedback, selectedFeedback]);

  // Infinite scroll setup with optimized loading
  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore,
    onLoadMore: () => fetchFeedbackData(page + 1),
    disabled: !!searchQuery, // Disable infinite scroll when searching
    rootMargin: '0px 0px 400px 0px',
  });

  // Optimized resize handlers with RAF
  const handleMouseDown = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.pageX;
    startWidth.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftPanelWidth]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;

    requestAnimationFrame(() => {
      const deltaX = e.pageX - startX.current;
      const newWidth = Math.max(300, Math.min(600, startWidth.current + deltaX));
      setLeftPanelWidth(newWidth);
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Handle refresh with loading state and toast
  const handleRefresh = useCallback(async () => {
    try {
      setIsLoading(true);
      await fetchFeedbackData(1);
      toast({
        title: "Refreshed",
        description: "Feedback data has been refreshed.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh feedback data.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchFeedbackData, toast]);

  // Auto-collapse stats on scroll
  const handleScroll = useCallback((e) => {
    const currentScroll = e.target.scrollTop;
    if (Math.abs(currentScroll - lastScrollY.current) > scrollThreshold) {
      if (currentScroll > lastScrollY.current && isStatsExpanded) {
        setIsStatsExpanded(false);
      } else if (currentScroll < lastScrollY.current && !isStatsExpanded) {
        setIsStatsExpanded(true);
      }
      lastScrollY.current = currentScroll;
    }
  }, [isStatsExpanded]);

  return (
    <div className="flex h-screen bg-background">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <FeedbackHeader
          isStatsExpanded={isStatsExpanded}
          onToggleStats={() => setIsStatsExpanded(!isStatsExpanded)}
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          onRefresh={handleRefresh}
          onCreateSampleData={handleCreateSampleData}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          isLoading={isLoading}
          stats={stats}
          isMobile={isMobile}
        />

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - List View */}
          <div
            className={cn(
              "flex flex-col bg-background",
              isMobile && showDetailView ? "hidden" : "w-full md:w-[400px]",
              !isMobile && "w-[400px]"
            )}
          >
            {/* Feedback List */}
            <div
              className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-border/40 scrollbar-track-transparent hover:scrollbar-thumb-border/60"
              onScroll={handleScroll}
            >
              <div className="py-2">
                {filteredFeedback.length > 0 ? (
                  <div className="space-y-1">
                    {filteredFeedback.map((feedback) => (
                      <motion.div
                        key={feedback.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <FeedbackListItem
                          feedback={feedback}
                          isSelected={selectedFeedback?.id === feedback.id}
                          onClick={() => {
                            handleFeedbackSelect(feedback);
                            if (isMobile) {
                              setShowDetailView(true);
                            }
                          }}
                          isMobile={isMobile}
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-12 text-muted-foreground px-4">
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center mx-auto">
                        <MessageSquare className="h-6 w-6 opacity-50" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-foreground mb-1">No Feedback Found</h3>
                        <p className="text-sm text-muted-foreground mb-4">No feedback matches your current filters</p>
                        <Button
                          variant="outline"
                          onClick={handleCreateSampleData}
                          className="gap-2 h-10 px-4"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Create Sample Data
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {isLoading && (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                <div ref={infiniteRef} className="h-4" />
              </div>
            </div>
          </div>

          {/* Right Panel - Detail View */}
          <div className={cn(
            "flex-1 flex flex-col bg-background",
            isMobile && !showDetailView && "hidden"
          )}>
            {selectedFeedback ? (
              <>
                <FeedbackDetailView
                  feedback={selectedFeedback}
                  onClose={() => {
                    setSelectedFeedback(null);
                    if (isMobile) {
                      setShowDetailView(false);
                    }
                  }}
                  onStatusUpdate={handleStatusUpdate}
                  isMobile={isMobile}
                />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground px-4">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center mx-auto">
                    <MessageSquare className="h-6 w-6 opacity-50" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-1">No Feedback Selected</h3>
                    <p className="text-sm text-muted-foreground">Choose a feedback item to view details</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;